import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map the frontend bookingData snapshot (camelCase) to the backend BookingCreate model (snake_case).
const buildBookingCreatePayload = (bookingData, customerInfo, sessionId) => {
  // Convert each package from frontend shape to backend Package shape
  const packages = (bookingData.packages || []).map((pkg, idx) => ({
    package_number: pkg.packageNumber || idx + 1,
    package_type: pkg.package_type || 'label',
    carrier: pkg.carrier || null,
    tracking_last6: pkg.tracking_last6 || null,
    qr_image: pkg.qr_image || null,
    label_url: pkg.label_url || null,
    item_description: pkg.item_description || pkg.notes || '',
    has_lithium_batteries: !!pkg.hasLithiumBatteries,
    is_fragile: !!pkg.isFragile,
    needs_packaging: !!pkg.packaging_size,
    package_size: pkg.packaging_size || null,
    status: 'pending_pickup',
  }));

  return {
    user_email: bookingData.customerEmail || customerInfo.email || '',
    user_name: customerInfo.name || '',
    user_phone: customerInfo.phone || '',
    pickup_date: bookingData.pickupDate || '',
    pickup_window: bookingData.pickupWindow || '',
    location_name: bookingData.locationName || '',
    location_address: bookingData.locationAddress || '',
    number_of_packages: bookingData.numberOfPackages || packages.length,
    packaging_count: bookingData.packagingCount || 0,
    packaging_cost: bookingData.packagingCost || 0.0,
    has_oversized_package: !!bookingData.hasOversizedPackage,
    rush_dropoff: !!bookingData.rushDropoff,
    total_cost: bookingData.totalCost || 0.0,
    stripe_session_id: sessionId || null,
    packages,
  };
};

const ConfirmationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [bookingData, setBookingData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [error, setError] = useState('');

  useEffect(() => {
    const finalizeBooking = async () => {
      const sessionId = searchParams.get('session_id');

      // Load the bookingData snapshot that SchedulePickup stored before payment
      const storedData = sessionStorage.getItem('bookingData');
      if (!storedData) {
        if (!sessionId) {
          navigate('/');
        } else {
          setError('Booking data missing — please contact support@getreturnpal.com');
          setPaymentStatus('failed');
        }
        return;
      }

      let parsed;
      try {
        parsed = JSON.parse(storedData);
      } catch {
        setError('Unable to load booking data');
        setPaymentStatus('failed');
        return;
      }
      setBookingData(parsed);

      // If we don't have a Stripe session_id, just show the page (rare path — no payment)
      if (!sessionId) {
        setPaymentStatus('success');
        return;
      }

      // Idempotency: only create the booking once per Stripe session, even on refresh
      const alreadyCreated = sessionStorage.getItem(`bookingCreated:${sessionId}`);
      if (alreadyCreated) {
        try {
          const cached = JSON.parse(alreadyCreated);
          setBookingData({ ...parsed, ...cached });
        } catch { /* ignore */ }
        setPaymentStatus('success');
        return;
      }

      // POST the booking now that Stripe payment succeeded
      try {
        const customerInfo = JSON.parse(sessionStorage.getItem('customerInfo') || '{}');
        const payload = buildBookingCreatePayload(parsed, customerInfo, sessionId);

        const res = await fetch(`${API_URL}/api/bookings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const errText = await res.text();
          throw new Error(`Booking creation failed (${res.status}): ${errText}`);
        }

        const booking = await res.json();
        // Merge confirmation_number + booking_id + packages (with returnpal_qr_id) back into local state
        const merged = {
          ...parsed,
          id: booking.id,
          confirmation_number: booking.confirmation_number,
          packages: booking.packages || parsed.packages,
        };
        setBookingData(merged);
        sessionStorage.setItem('bookingData', JSON.stringify(merged));
        sessionStorage.setItem(
          `bookingCreated:${sessionId}`,
          JSON.stringify({
            id: booking.id,
            confirmation_number: booking.confirmation_number,
            packages: booking.packages,
          }),
        );
        setPaymentStatus('success');
      } catch (err) {
        setError(err.message);
        setPaymentStatus('failed');
      }
    };

    finalizeBooking();

    // Mark abandoned cart as complete (best-effort)
    const cartId = sessionStorage.getItem('resumedCartId');
    if (cartId) {
      fetch(`${API_URL}/api/abandoned-carts/${cartId}/complete`, { method: 'POST' })
        .catch(() => {})
        .finally(() => sessionStorage.removeItem('resumedCartId'));
    }
  }, [navigate, searchParams]);

  const getInstructionTime = () => {
    if (!bookingData) return '';
    return bookingData.pickupWindow?.includes('Midday') 
      ? 'prior to 11:30 AM for midday collection' 
      : 'prior to 4:00 PM for end-of-day collection';
  };

  if (!bookingData) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          {paymentStatus === 'checking' && <h1>Verifying Payment...</h1>}
          {paymentStatus === 'failed' && (
            <>
              <h1>Payment Failed</h1>
              <p style={{ color: '#c00' }}>{error}</p>
              <Button onClick={() => navigate('/payment')} className="btn-primary">
                Try Again
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      padding: '2rem 1rem'
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Success Header with Logo */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{
            display: 'inline-block',
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '50%',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
            marginBottom: '1.5rem'
          }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_returnpal-pickup/artifacts/x8ux114u_Screenshot%202026-04-27%20165324.png" 
              alt="ReturnPal" 
              style={{ width: '120px', height: 'auto' }}
            />
          </div>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: '#065f46',
            marginBottom: '0.5rem'
          }}>
            ✓ You're All Set!
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#047857' }}>
            Booking #{bookingData.confirmation_number || 'Pending'}
          </p>
        </div>

        {/* Simple 3-Step Instructions */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '2.5rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          marginBottom: '2rem'
        }}>
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            📋 What to Do Next
          </h2>

          {/* Step 1 */}
          <div style={{
            display: 'flex',
            gap: '1.5rem',
            marginBottom: '2rem',
            paddingBottom: '2rem',
            borderBottom: '2px solid #f3f4f6'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#10b981',
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              flexShrink: 0
            }}>
              1
            </div>
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.75rem' }}>
                Write ID on Your Package
              </h3>
              <p style={{ color: '#4b5563', marginBottom: '1rem', lineHeight: '1.6' }}>
                Use a <strong>permanent marker</strong> to write one of these on the <strong>TOP and SIDE</strong> of each package:
              </p>
              <div style={{
                backgroundColor: '#fef3c7',
                padding: '1rem',
                borderRadius: '8px',
                border: '2px solid #fbbf24'
              }}>
                {bookingData.packages && bookingData.packages.map((pkg, idx) => {
                  const packageType = pkg.package_type || 'label';
                  const idToWrite = packageType === 'qr' 
                    ? (pkg.returnpal_qr_id || `RP-${bookingData.confirmation_number}-${idx + 1}`)
                    : (pkg.tracking_last6 || 'XXXXXX');
                  return (
                    <p key={pkg.package_number || `pkg-${idx}`} style={{ 
                      fontSize: '1.125rem', 
                      fontWeight: 'bold', 
                      color: '#92400e',
                      margin: '0.5rem 0'
                    }}>
                      Package {idx + 1}: <span style={{ fontSize: '1.5rem' }}>{idToWrite}</span>
                    </p>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div style={{
            display: 'flex',
            gap: '1.5rem',
            marginBottom: '2rem',
            paddingBottom: '2rem',
            borderBottom: '2px solid #f3f4f6'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#10b981',
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              flexShrink: 0
            }}>
              2
            </div>
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.75rem' }}>
                Bring to {bookingData.locationName}
              </h3>
              <p style={{ color: '#4b5563', lineHeight: '1.6' }}>
                Drop off your package at the <strong>reception</strong> by <strong>{getInstructionTime()}</strong>
                {bookingData.locationName?.includes('Scottsdale Quarter Businesses') && (
                  <><br /><em style={{ color: '#dc2626' }}>Leave with your business reception, front desk, or cashier</em></>
                )}
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div style={{
            display: 'flex',
            gap: '1.5rem'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#10b981',
              color: 'white',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              flexShrink: 0
            }}>
              3
            </div>
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.75rem' }}>
                We Handle the Rest
              </h3>
              <p style={{ color: '#4b5563', lineHeight: '1.6', marginBottom: '0.75rem' }}>
                • ReturnPal picks up on <strong>{bookingData.pickupDate}</strong> during {bookingData.pickupWindow}<br />
                • We attach your label and deliver to the carrier within 48 hours<br />
                • You'll receive an email when delivered
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
          <Button 
            onClick={() => {
              sessionStorage.removeItem('bookingData');
              navigate('/dashboard');
            }} 
            className="btn-primary"
            style={{ padding: '1rem', fontSize: '1rem' }}
          >
            View My Returns
          </Button>
          <Button 
            onClick={() => {
              sessionStorage.clear();
              navigate('/');
            }} 
            style={{ 
              padding: '1rem', 
              fontSize: '1rem',
              backgroundColor: 'white',
              color: '#10b981',
              border: '2px solid #10b981'
            }}
          >
            Return to Home
          </Button>
        </div>

        {/* Confirmation Email Note */}
        <div style={{
          backgroundColor: '#e0f2fe',
          padding: '1.5rem',
          borderRadius: '12px',
          textAlign: 'center',
          border: '1px solid #0ea5e9'
        }}>
          <p style={{ color: '#0c4a6e', fontWeight: '500' }}>
            📧 Check your email for full booking details and instructions
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationPage;
